import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(function (o, c) {
  // todo support Wo (ISO week)
  var proto = c.prototype;
  var oldFormat = proto.format;
  proto.format = function f(formatStr) {
    var str = (formatStr || '').replace('Wo', 'wo');
    return oldFormat.bind(this)(str);
  };
});
var localeMap = {
  // ar_EG:
  // az_AZ:
  // bg_BG:
  bn_BD: 'bn-bd',
  by_BY: 'be',
  // ca_ES:
  // cs_CZ:
  // da_DK:
  // de_DE:
  // el_GR:
  en_GB: 'en-gb',
  en_US: 'en',
  // es_ES:
  // et_EE:
  // fa_IR:
  // fi_FI:
  fr_BE: 'fr',
  // todo: dayjs has no fr_BE locale, use fr at present
  fr_CA: 'fr-ca',
  // fr_FR:
  // ga_IE:
  // gl_ES:
  // he_IL:
  // hi_IN:
  // hr_HR:
  // hu_HU:
  hy_AM: 'hy-am',
  // id_ID:
  // is_IS:
  // it_IT:
  // ja_JP:
  // ka_GE:
  // kk_KZ:
  // km_KH:
  kmr_IQ: 'ku',
  // kn_IN:
  // ko_KR:
  // ku_IQ: // previous ku in antd
  // lt_LT:
  // lv_LV:
  // mk_MK:
  // ml_IN:
  // mn_MN:
  // ms_MY:
  // nb_NO:
  // ne_NP:
  nl_BE: 'nl-be',
  // nl_NL:
  // pl_PL:
  pt_BR: 'pt-br',
  // pt_PT:
  // ro_RO:
  // ru_RU:
  // sk_SK:
  // sl_SI:
  // sr_RS:
  // sv_SE:
  // ta_IN:
  // th_TH:
  // tr_TR:
  // uk_UA:
  // ur_PK:
  // vi_VN:
  zh_CN: 'zh-cn',
  zh_HK: 'zh-hk',
  zh_TW: 'zh-tw'
};
var parseLocale = function parseLocale(locale) {
  var mapLocale = localeMap[locale];
  return mapLocale || locale.split('_')[0];
};

/* istanbul ignore next */
var parseNoMatchNotice = function parseNoMatchNotice() {
  // zombieJ:
  // When user typing, its always miss match format.
  // This check is meaningless.
  // https://github.com/ant-design/ant-design/issues/51839
  // noteOnce(false, 'Not match any format. Please help to fire a issue about this.');
};

// Use internal dayjs instance to avoid implicit dependency on plugins extended by external dayjs versions
var getUDayjs = function getUDayjs(value) {
  if (!dayjs.isDayjs(value) || value instanceof dayjs) {
    return value;
  }
  return dayjs(value.valueOf());
};
var generateConfig = {
  // get
  getNow: function getNow() {
    var now = dayjs();
    // https://github.com/ant-design/ant-design/discussions/50934
    if (typeof now.tz === 'function') {
      return now.tz(); // use default timezone
    }
    return now;
  },
  getFixedDate: function getFixedDate(string) {
    return dayjs(string, ['YYYY-M-DD', 'YYYY-MM-DD']);
  },
  getEndDate: function getEndDate(date) {
    return getUDayjs(date).endOf('month');
  },
  getWeekDay: function getWeekDay(date) {
    var clone = getUDayjs(date).locale('en');
    return clone.weekday() + clone.localeData().firstDayOfWeek();
  },
  getYear: function getYear(date) {
    return getUDayjs(date).year();
  },
  getMonth: function getMonth(date) {
    return getUDayjs(date).month();
  },
  getDate: function getDate(date) {
    return getUDayjs(date).date();
  },
  getHour: function getHour(date) {
    return getUDayjs(date).hour();
  },
  getMinute: function getMinute(date) {
    return getUDayjs(date).minute();
  },
  getSecond: function getSecond(date) {
    return getUDayjs(date).second();
  },
  getMillisecond: function getMillisecond(date) {
    return getUDayjs(date).millisecond();
  },
  // set
  addYear: function addYear(date, diff) {
    return getUDayjs(date).add(diff, 'year');
  },
  addMonth: function addMonth(date, diff) {
    return getUDayjs(date).add(diff, 'month');
  },
  addDate: function addDate(date, diff) {
    return getUDayjs(date).add(diff, 'day');
  },
  setYear: function setYear(date, year) {
    return getUDayjs(date).year(year);
  },
  setMonth: function setMonth(date, month) {
    return getUDayjs(date).month(month);
  },
  setDate: function setDate(date, num) {
    return getUDayjs(date).date(num);
  },
  setHour: function setHour(date, hour) {
    return getUDayjs(date).hour(hour);
  },
  setMinute: function setMinute(date, minute) {
    return getUDayjs(date).minute(minute);
  },
  setSecond: function setSecond(date, second) {
    return getUDayjs(date).second(second);
  },
  setMillisecond: function setMillisecond(date, milliseconds) {
    return getUDayjs(date).millisecond(milliseconds);
  },
  // Compare
  isAfter: function isAfter(date1, date2) {
    return getUDayjs(date1).isAfter(getUDayjs(date2));
  },
  isValidate: function isValidate(date) {
    return getUDayjs(date).isValid();
  },
  locale: {
    getWeekFirstDay: function getWeekFirstDay(locale) {
      return dayjs().locale(parseLocale(locale)).localeData().firstDayOfWeek();
    },
    getWeekFirstDate: function getWeekFirstDate(locale, date) {
      return getUDayjs(date).locale(parseLocale(locale)).weekday(0);
    },
    getWeek: function getWeek(locale, date) {
      return getUDayjs(date).locale(parseLocale(locale)).week();
    },
    getShortWeekDays: function getShortWeekDays(locale) {
      return dayjs().locale(parseLocale(locale)).localeData().weekdaysMin();
    },
    getShortMonths: function getShortMonths(locale) {
      return dayjs().locale(parseLocale(locale)).localeData().monthsShort();
    },
    format: function format(locale, date, _format) {
      return getUDayjs(date).locale(parseLocale(locale)).format(_format);
    },
    parse: function parse(locale, text, formats) {
      var localeStr = parseLocale(locale);
      for (var i = 0; i < formats.length; i += 1) {
        var format = formats[i];
        var formatText = text;
        if (format.includes('wo') || format.includes('Wo')) {
          // parse Wo
          var year = formatText.split('-')[0];
          var weekStr = formatText.split('-')[1];
          var firstWeek = dayjs(year, 'YYYY').startOf('year').locale(localeStr);
          for (var j = 0; j <= 52; j += 1) {
            var nextWeek = firstWeek.add(j, 'week');
            if (nextWeek.format('Wo') === weekStr) {
              return nextWeek;
            }
          }
          parseNoMatchNotice();
          return null;
        }
        var date = dayjs(formatText, format, true).locale(localeStr);
        if (date.isValid()) {
          return date;
        }
      }
      if (text) {
        parseNoMatchNotice();
      }
      return null;
    }
  }
};
export default generateConfig;